import { useMemo, useState } from 'react'
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  toast,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@you/ui'
import {
  useDeleteMembers,
  useSaveOrUpdateMember,
  useMoveMembers,
  useDepartmentTree,
  useDownloadMemberTemplate,
  useImportMember,
  useMemberList,
} from '@/api/hooks/useOrganization'
import { useOrganizationStore } from '@/stores/organization'
import { Search } from 'lucide-react'
import type {
  DepartmentTreeNode,
  GetMemberListRequest,
} from '@/api/types/organization.type'
import ImportDialog from '@/components/common/ImportDialog'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { downloadTemplate as downloadTemplateUtil } from '@/lib/utils'

const MemberList = () => {
  const { currentOrganization, currentDepartment } = useOrganizationStore()
  const { currentWorkspace } = useWorkspaceStore()
  const [search, setSearch] = useState('')
  const [page] = useState(1)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | undefined>(undefined)
  const [formName, setFormName] = useState('')
  const [formPhone, setFormPhone] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [idsToDelete, setIdsToDelete] = useState<string[]>([])
  const [copyPopoverOpen, setCopyPopoverOpen] = useState(false)
  const [movePopoverOpen, setMovePopoverOpen] = useState(false)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const pageSize = 20
  const hasDept = currentDepartment?.id
  const isRoot = useMemo(() => currentDepartment?.isRoot, [currentDepartment])
  const params = hasDept
    ? ({
        teamId: currentDepartment!.id,
        departmentId: currentDepartment!.id,
    page: String(page),
    pageSize,
    search,
      } as GetMemberListRequest)
    : undefined

  const { data, mutate } = useMemberList(params, isRoot)
  const members = useMemo(() => data?.members || [], [data])
  const { trigger: deleteMembers } = useDeleteMembers()
  const { trigger: saveOrUpdateMember } = useSaveOrUpdateMember()
  const { trigger: moveMembers } = useMoveMembers()
  const { trigger: downloadTemplate } = useDownloadMemberTemplate()
  const { trigger: importMember } = useImportMember()
  const { data: deptTree } = useDepartmentTree({
    teamId: currentOrganization?.id || '',
  })

  const title = useMemo(
    () => currentDepartment?.name || '全部成员',
    [currentDepartment]
  )
  const allSelected = useMemo(
    () =>
      members.length > 0 && members.every((m) => selectedIds.includes(m.id)),
    [members, selectedIds]
  )

  // handlers（统一提前定义，减少内联绑定，便于维护）
  const handleSearchChange = (value: string) => setSearch(value)

  const handleToggleAll = () => {
    if (allSelected) setSelectedIds([])
    else setSelectedIds(members.map((m) => m.id))
  }

  const handleToggleOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id)
    )
  }

  // 打开删除确认（支持单个和批量）
  const openDeleteConfirm = (ids: string[]) => {
    if (ids.length === 0) return
    setIdsToDelete(ids)
    setIsDeleteOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (idsToDelete.length === 0) return
    await deleteMembers(idsToDelete)
    setSelectedIds((prev) => prev.filter((x) => !idsToDelete.includes(x)))
    setIsDeleteOpen(false)
    setIdsToDelete([])
    toast.success('删除成功')
    await mutate()
  }

  const openCreateDialog = () => {
    if (!currentDepartment?.id) {
      toast.error('请先在左侧选择一个部门')
      return
    }
    setEditingId(undefined)
    setFormName('')
    setFormPhone('')
    setIsDialogOpen(true)
  }

  const openEditDialog = (id: string) => {
    const current = members.find((m) => m.id === id)
    setEditingId(id)
    setFormName(current?.name || '')
    setFormPhone(current?.phone || '')
    setIsDialogOpen(true)
  }

  const handleSubmitDialog = async () => {
    const phone = formPhone.trim()
    if (!formName.trim()) {
      toast.error('请输入姓名')
      return
    }
    if (!phone) {
      toast.error('请输入联系电话')
      return
    }
    // 中国大陆手机号码校验：1开头，第二位3-9，共11位
    const phoneRegex = /^1[3-9]\d{9}$/
    if (!phoneRegex.test(phone)) {
      toast.error('请输入有效的手机号（11位大陆手机号码）')
      return
    }
    setIsSubmitting(true)
    try {
      await saveOrUpdateMember({
        id: editingId,
        name: formName.trim(),
        phone,
        departmentId: currentDepartment?.id,
        teamId: currentOrganization?.id,
      })
      setIsDialogOpen(false)
      setEditingId(undefined)
      setFormName('')
      setFormPhone('')
      toast.success('保存成功')
      await mutate()
    } finally {
      setIsSubmitting(false)
    }
  }

  // 成员导入相关方法
  const handleDownloadTemplate = async () => {
    try {
      const result = await downloadTemplate()
      await downloadTemplateUtil(result)
      toast.success('模板下载成功')
    } catch {
      toast.error('模板下载失败')
    }
  }

  const handleImportMember = async (file: File) => {
    if (
      !currentDepartment?.id ||
      !currentOrganization?.id ||
      !currentWorkspace?.id
    ) {
      toast.error('缺少必要的组织信息')
      return
    }

    try {
      await importMember({
        departmentId: Number(currentDepartment.id),
        teamId: Number(currentOrganization.id),
        workspaceId: Number(currentWorkspace.id),
        file,
      })
      await mutate()
    } catch {
      toast.error('成员导入失败')
    }
  }

  const openImportDialog = () => {
    if (!currentDepartment?.id) {
      toast.error('请先在左侧选择一个部门')
      return
    }
    setImportDialogOpen(true)
  }

  // 扁平化部门树用于在 Popover 中展示
  const flattenDepartments = (
    nodes: DepartmentTreeNode[] | undefined,
    acc: { id: string; name: string; level: number }[] = [],
    level = 0
  ) => {
    if (!nodes) return acc
    for (const n of nodes) {
      acc.push({ id: n.id, name: n.name, level })
      // 兼容字段 subDepartments，避免使用 any
      const children: DepartmentTreeNode[] | undefined =
        (n as DepartmentTreeNode & { subDepartments?: DepartmentTreeNode[] })
          .subDepartments ?? (n as DepartmentTreeNode).subDepartments
      if (children && children.length > 0) {
        flattenDepartments(children, acc, level + 1)
      }
    }
    return acc
  }
  const deptOptions = useMemo(
    () => flattenDepartments(deptTree?.departments),
    [deptTree]
  )

  const handleMoveTo = async (toDepartmentId: string, isCopy: boolean) => {
    if (selectedIds.length === 0) return
    await moveMembers({
      isCopy: isCopy ? 1 : 0,
      memberIds: selectedIds,
      toDepartmentId,
    })
    setSelectedIds([])
    setCopyPopoverOpen(false)
    setMovePopoverOpen(false)
    await mutate()
    toast.success(isCopy ? '复制并移动成功' : '移动成功')
  }

  // 当未选择部门时，整个页面显示空提示，且不触发列表请求（SWR key 为 null）
  if (!hasDept) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-500">
        请先在左侧选择一个部门
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 h-full">
      {/* 上 */}
      <div className="flex items-center justify-between">
        <div className="text-[20px] font-medium" style={{ color: '#3D1E82' }}>
          {title}
        </div>
        <div className="flex items-center gap-3 transition-all duration-200">
          {selectedIds.length > 0 ? (
            <div className="flex items-center gap-3 opacity-100 translate-y-0">
              <Button
                variant="outline"
                onClick={() => openDeleteConfirm(selectedIds)}
                className="text-[#EB6841] border-[#EB6841]"
              >
                批量删除
              </Button>

              {/* 复制并移动到 */}
              <Popover open={copyPopoverOpen} onOpenChange={setCopyPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline">复制并移动到</Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2">
                  <div className="max-h-64 overflow-auto text-sm">
                    {deptOptions?.map((d) => (
                      <button
                        key={d.id}
                        className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F7F1FF]"
                        onClick={() => handleMoveTo(d.id, true)}
                        disabled={d.id === currentDepartment?.id}
                        title={
                          d.id === currentDepartment?.id
                            ? '已在当前部门'
                            : undefined
                        }
                        style={{ paddingLeft: 8 + d.level * 12 }}
                      >
                        {d.name}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* 移动到 */}
              <Popover open={movePopoverOpen} onOpenChange={setMovePopoverOpen}>
                <PopoverTrigger asChild>
                  <Button className="gradient-gentle">移动到</Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2">
                  <div className="max-h-64 overflow-auto text-sm">
                    {deptOptions?.map((d) => (
                      <button
                        key={d.id}
                        className="w-full text-left px-2 py-1.5 rounded hover:bg-[#F7F1FF]"
                        onClick={() => handleMoveTo(d.id, false)}
                        disabled={d.id === currentDepartment?.id}
                        title={
                          d.id === currentDepartment?.id
                            ? '已在当前部门'
                            : undefined
                        }
                        style={{ paddingLeft: 8 + d.level * 12 }}
                      >
                        {d.name}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          ) : (
            <div className="flex items-center gap-3 opacity-100 translate-y-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="搜索成员"
            value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="px-0 w-[260px] pl-9 pr-2"
          />
              </div>
              {!currentDepartment?.isRoot && (
                <>
          <Button
                    className="gradient-gentle"
                    onClick={openImportDialog}
          >
            导入
          </Button>
          <Button
                    className="gradient-gentle"
                    onClick={openCreateDialog}
          >
            新增
          </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 下：自定义表格（非 @you/ui） */}
      <div className="bg-white rounded-lg flex-1 overflow-hidden px-4 py-2">
        {/* 骨架屏：数据加载中 */}
        {!data ? (
          <div className="p-4">
            {/* 表头骨架 */}
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
            </div>
            {/* 表格行骨架 */}
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="flex items-center space-x-4 mb-3">
                <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-8 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        ) : members.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-gray-500">
            暂无成员
          </div>
        ) : (
          <table
            className="w-full border text-[#333333] text-[14px]"
            style={{ borderColor: '#EFF3FD' }}
          >
            <thead>
              <tr
                style={{
                  height: '38px',
                  backgroundColor: '#F7F9FF',
                  color: '#333333',
                }}
                className="text-xs"
              >
                <th
                  className="w-14 text-center border"
                  style={{ borderColor: '#EFF3FD' }}
                >
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleToggleAll}
                    style={{ accentColor: '#8047FF' }}
                  />
                </th>
                <th
                  className="text-center border"
                  style={{ borderColor: '#EFF3FD' }}
                >
                  编号
                </th>
                <th
                  className="text-center border"
                  style={{ borderColor: '#EFF3FD' }}
                >
                  部门
                </th>
                <th
                  className="text-center border"
                  style={{ borderColor: '#EFF3FD' }}
                >
                  姓名
                </th>
                <th
                  className="text-center border"
                  style={{ borderColor: '#EFF3FD' }}
                >
                  手机号码
                </th>
                <th
                  className="text-center border w-40"
                  style={{ borderColor: '#EFF3FD' }}
                >
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {members.map((m, idx) => (
                <tr
                  key={m.id}
                  className="[&>td]:text-center"
                  style={{
                    height: '40px',
                    color: '#333333',
                    backgroundColor: idx % 2 === 1 ? '#FBFBFB' : 'transparent',
                  }}
                >
                  <td className="border" style={{ borderColor: '#EFF3FD' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(m.id)}
                      onChange={(e) => handleToggleOne(m.id, e.target.checked)}
                      style={{ accentColor: '#8047FF' }}
                    />
                  </td>
                  <td className="border" style={{ borderColor: '#EFF3FD' }}>
                    {idx + 1}
                  </td>
                  <td className="border" style={{ borderColor: '#EFF3FD' }}>
                    {m.departmentName}
                  </td>
                  <td className="border" style={{ borderColor: '#EFF3FD' }}>
                    {m.name}
                  </td>
                  <td className="border" style={{ borderColor: '#EFF3FD' }}>
                    {m.phone}
                  </td>
                  <td className="border" style={{ borderColor: '#EFF3FD' }}>
                    <div className="flex items-center justify-center gap-4">
                      <button
                        className="text-[#2C6BFF]"
                        onClick={() => openEditDialog(m.id)}
                      >
                        编辑
                      </button>
                      <button
                        className="text-[#EB6841]"
                        onClick={() => openDeleteConfirm([m.id])}
                      >
                        删除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 新增/编辑成员对话框 */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader title={editingId ? '编辑成员' : '新增成员'} />
          <div className="px-4 py-2 space-y-4">
            <div>
              <label className="block text-sm text-gray-700 mb-2">
                姓名 <span className="text-red-500">*</span>
              </label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="请输入姓名"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-2">
                联系电话 <span className="text-red-500">*</span>
              </label>
              <Input
                value={formPhone}
                onChange={(e) => setFormPhone(e.target.value)}
                placeholder="请输入联系电话"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setIsDialogOpen(false)}
              disabled={isSubmitting}
            >
              取消
            </Button>
            <Button onClick={handleSubmitDialog} disabled={isSubmitting}>
              {isSubmitting ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除确认对话框 */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader title="删除确认" />
          <div className="py-2 text-center text-base text-gray-600">
            删除后数据无法恢复，确认要删除所选数据吗？
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsDeleteOpen(false)}>
              取消
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              确定删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 成员导入对话框 */}
      <ImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        title="导入成员"
        onImport={handleImportMember}
        onDownloadTemplate={handleDownloadTemplate}
        accept=".xlsx,.xls"
        maxSize={20}
      />
    </div>
  )
}

export default MemberList
